import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ReceiptUploader = ({ 
  receipt, 
  onReceiptChange, 
  error 
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    
    const files = e?.dataTransfer?.files;
    if (files?.length > 0) {
      handleFileSelection(files?.[0]);
    }
  };

  const handleFileSelection = (file) => {
    if (file && file?.type?.startsWith('image/')) {
      onReceiptChange(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleRemove = () => {
    onReceiptChange(null);
    setPreview(null);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Upload Receipt <span className="text-error">*</span>
        </label>
        <p className="text-xs md:text-sm text-muted-foreground mb-3">
          Clear photo showing purchase details, date, and total amount
        </p>
      </div>
      {!preview ? (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-6 md:p-8 transition-all ${
            isDragging 
              ? 'border-primary bg-primary/10' 
              : error 
              ? 'border-error bg-error/5' :'border-border bg-muted/30 hover:border-primary/50'
          }`}
        >
          <div className="flex flex-col items-center text-center gap-4">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Upload" size={32} color="var(--color-primary)" />
            </div>
            
            <div>
              <p className="text-sm md:text-base font-medium text-foreground mb-1">
                Drag and drop your receipt here
              </p>
              <p className="text-xs md:text-sm text-muted-foreground">
                or click to browse files
              </p>
            </div>

            <Button
              variant="outline"
              iconName="Image"
              onClick={() => fileInputRef?.current?.click()}
            >
              Choose File
            </Button>

            <div className="hidden md:block">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon name="Camera" size={14} />
                <span>Mobile: Use camera to capture receipt instantly</span>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>
        </div>
      ) : (
        <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
          <div className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="aspect-[3/4] max-h-64 md:max-h-80 overflow-hidden rounded-lg bg-muted">
                <Image
                  src={preview}
                  alt="Receipt preview showing purchase details and transaction information"
                  className="w-full h-full object-contain"
                />
              </div>
              
              <div className="mt-4 flex items-center gap-2 text-xs md:text-sm text-success">
                <Icon name="CheckCircle" size={16} />
                <span>Receipt uploaded successfully</span>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              iconName="RefreshCw"
              onClick={() => fileInputRef?.current?.click()}
              fullWidth
            >
              Replace
            </Button>
            <Button
              variant="destructive"
              iconName="Trash2"
              onClick={handleRemove}
              fullWidth
            >
              Remove
            </Button>
          </div>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-error/10 rounded-lg border border-error/20">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
          <p className="text-xs md:text-sm text-error">{error}</p>
        </div>
      )}
    </div>
  );
};

export default ReceiptUploader;